/**
 * Stripe Payment Service
 * Handles all Stripe-related payment operations
 */

class StripeService {
  constructor() {
    this.paymentsApiUrl = process.env.REACT_APP_PAYMENTS_API_URL;
    if (!this.paymentsApiUrl) {
      console.error('REACT_APP_PAYMENTS_API_URL is not configured');
    }
  }

  /**
   * Create a Stripe checkout session
   * @param {Object} params
   * @param {string} params.title
   * @param {string} params.priceId
   * @param {number} params.quantity
   * @param {string} params.customerEmail - Optional customer email
   * @returns {Promise<string>}
   */
  async createCheckoutSession({ title, priceId, quantity = 1, customerEmail }) {
    if (!priceId) {
      throw new Error('Price ID is required');
    }

    if (!this.paymentsApiUrl) {
      throw new Error('Payment API URL is not configured. Please check your environment variables.');
    }

    try {
      const response = await fetch(`${this.paymentsApiUrl}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title, priceId, quantity, customerEmail }),
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.url) {
        throw new Error('No checkout URL returned from server');
      }

      return data.url;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      throw error;
    }
  }

  /**
   * Redirect to Stripe checkout
   * @param {Object} params
   */
  async redirectToCheckout(params) {
    try {
      const checkoutUrl = await this.createCheckoutSession(params);
      window.location.href = checkoutUrl;
    } catch (error) {
      throw new Error(`Payment initialization failed: ${error.message}`);
    }
  }
}

export default new StripeService();
